@if (initialized && customerBillingAccountDto) {
    <!-- Dunning Object Sub-View -->
    @if (showDunningObject && dunningObjectIdentifier) {
        <div class="mb-4">
            <p-breadcrumb [model]="dunningBreadcrumb" styleClass="surface-card border-round-xl shadow-1 border-1 surface-border"></p-breadcrumb>
        </div>
        <dunning-object [objectIdentifier]="dunningObjectIdentifier"></dunning-object>
    } @else {
    <!-- Seitenkopf -->
    <div class="flex flex-wrap justify-content-between align-items-start gap-4 surface-card p-4 border-round-xl shadow-1 border-1 surface-border mb-4">
        <div class="flex gap-3">
            <!--
              THE HEADER AVATAR: the BANK's logo when this account is collected from one, the generic
              payment-method icon otherwise.

              Why the bank wins when it exists: getPaymentMethodIcon() answers `pi pi-building` for
              EVERY Einzug, so an ING-DiBa and a Commerzbank account get byte-identical headers. The
              logo is what makes the account recognisable, and it costs nothing - the full view
              already loads the bm.CustomerBillingAccountBank time slice as a nested node (currentBankAccountDto).

              The gate is PRESENCE only: whenever the account carries a bank account with an image,
              its logo is shown, whatever the payment method says. The method is not lost by that -
              it stands right next to the title as its own tag, so a Selbstzahler stays labelled a
              Selbstzahler and is still recognisable by its bank.

              bm-bank-badge, not an <img>: it owns the FontAwesome-name-vs-URL rule and the initials
              fallback, and it is declared in this very module. showName=false because the bank name
              already stands in the subtitle line below, next to Kontoinhaber and IBAN.
            -->
            @if (getHeaderBankImage()) {
                <div class="p-2 border-round-lg flex align-items-center justify-content-center surface-100"
                     [style.opacity]="isSepaDirectDebitSuspended() ? '0.5' : '1'">
                    <bm-bank-badge
                            [bankImage]="getHeaderBankImage()"
                            [bankName]="getHeaderBankName()"
                            [showName]="false"
                            [size]="40">
                    </bm-bank-badge>
                </div>
            } @else {
                <div class="bg-primary-100 p-3 border-round-lg flex align-items-center justify-content-center">

                    <i [class]="getPaymentMethodIcon() + ' text-primary text-3xl'"
                       [style.opacity]="isSepaDirectDebitSuspended() ? '0.5' : '1'"></i>
                </div>
            }
            <div class="flex flex-column gap-1">
                <div class="flex align-items-center flex-wrap gap-3">
                    <h1 class="m-0 text-xl font-bold text-900">{{ customerBillingAccountDto.name || 'Konto #' + customerBillingAccountDto.id }}</h1>

                    @if (customerBillingAccountDto.legacyId) {
                        <div class="flex align-items-center gap-1 flex-shrink-0">
                            <i class="pi pi-key text-sm text-400"></i>
                            <span class="text-sm text-600">Legacy-ID: {{ customerBillingAccountDto.legacyId }}</span>
                        </div>
                        <span class="mx-2 text-300">|</span>
                    }

                    <!--
                      No "Aktiv"/"Geschlossen" status tag here any more: an open account is the normal
                      case and needs no badge, a closed one is announced by the red "Konto geschlossen"
                      message directly below the header, where it cannot be overlooked.
                    -->

                    <!--
                      The payment method as its OWN tag, next to the title.
                      It used to be the first word of the grey subtitle line, where it read as an
                      afterthought between Kontoinhaber and IBAN - yet it is the single fact that
                      decides whether this account is collected from at all. As a tag it is
                      unmistakably present; "warn" states the suspension the icon only hints at
                      through its opacity. The duplicate in the subtitle line below was dropped.
                    -->
                    <p-tag
                            [value]="getPaymentMethodLabel(customerBillingAccountDto.paymentMethod)"
                            [icon]="getPaymentMethodIcon()"
                            [severity]="isSepaDirectDebitSuspended() ? 'warn' : 'info'"
                            styleClass="text-xs font-bold">
                    </p-tag>
                    <!--
                      Payment day of the account's billing run type (1. / 15. / Monatsletzter) and the
                      payment frequencies of every assignment of this account that has not ENDED - active
                      today OR starting later (TM-1000002026, rule:
                      collectBmCurrentAndUpcomingPaymentFrequencies). Several frequencies can occur on one
                      account, each gets its own tag; a frequency that occurs on not-yet-started
                      assignments only reads „Monatlich ab 01.11.2026" and says so in its tooltip.
                    -->
                    @if (getPaymentDayHeaderLabel(); as paymentDayLabel) {
                        <p-tag
                                [value]="paymentDayLabel"
                                icon="fa-regular fa-calendar-day"
                                severity="secondary"
                                pTooltip="Zahlungstag laut Abrechnungslauftyp"
                                tooltipPosition="bottom"
                                styleClass="text-xs font-bold">
                        </p-tag>
                    }
                    <!-- TM-1000002026: one tag per frequency of the not-ended assignments, precomputed -->
                    @for (frequencyTag of contractFrequencyTags; track frequencyTag.frequency) {
                        <p-tag
                                [value]="frequencyTag.label"
                                icon="fa-regular fa-arrows-rotate"
                                severity="secondary"
                                [pTooltip]="frequencyTag.tooltip"
                                tooltipPosition="bottom"
                                styleClass="text-xs font-bold">
                        </p-tag>
                    }

                </div>
                <div class="flex align-items-center gap-2 text-500 text-base">
                    @if (customerBillingAccountDto.currentBankAccountDto?.customerBankAccountDto) {
                        <!--
                          THE ACCOUNT-HOLDER SLOT of the subtitle — the position the annotation's
                          arrow points at, between the payment method and the IBAN. It is rendered
                          RIGHT HERE and not as a tag up in the title row: the holder belongs to the
                          bank-account line it names, and read together with the IBAN it answers one
                          question ("whose account is collected from, and which one").

                          Two forms in the SAME slot. A deviant holder is a p-tag rather than plain
                          text because this is a grey text-500 line — the one header fact a clerk
                          must not have to open a tab to find would otherwise disappear into it.
                          A non-deviant holder is the customer themselves and stays plain text.

                          The name can be EMPTY on the bank account. A non-deviant holder without a
                          name renders NOTHING and takes its separator with it, so the line reads
                          "DE46…" rather than "| DE46…" — a separator with nothing on one side is
                          what made this line look broken. A DEVIANT holder without a name still
                          renders, label only: that the payer is somebody else is the decisive fact,
                          and it stands even when we cannot name them.
                        -->
                        @if (hasDeviantAccountHolder()) {
                            <p-tag
                                    [value]="getDeviantAccountHolderName()
                                        ? 'Abweichender Kontoinhaber: ' + getDeviantAccountHolderName()
                                        : 'Abweichender Kontoinhaber'"
                                    severity="warn"
                                    icon="pi pi-user-edit"
                                    styleClass="text-xs font-bold">
                            </p-tag>
                        } @else if (customerBillingAccountDto.currentBankAccountDto?.customerBankAccountDto.accountHolderName) {
                            <span>{{ customerBillingAccountDto.currentBankAccountDto?.customerBankAccountDto.accountHolderName }}</span>
                        }
                        <!-- The separator exists only when it actually separates two things. -->
                        @if ((hasDeviantAccountHolder() || customerBillingAccountDto.currentBankAccountDto?.customerBankAccountDto.accountHolderName)
                                && customerBillingAccountDto.currentBankAccountDto?.customerBankAccountDto.iban) {
                            <span class="text-300">|</span>
                        }
                        <span class="font-mono">{{ customerBillingAccountDto.currentBankAccountDto?.customerBankAccountDto.iban }}</span>
                        <!--
                          THE SLICE's validity, never the bank account's own dates (round 11, E-7 / D3):
                          currentBankAccountDto.startDate/endDate carry the bm.CustomerBillingAccountBank
                          slice valid today (attached in the base component), so bank account 1348 with
                          startDate = null still reads „gültig ab 01.01.1900". „bis" only for a real end.
                        -->
                        @if (headerBankValidFrom) {
                            <span class="text-300">|</span>
                            <span pTooltip="Gültigkeit der Bankverbindungs-Zeitscheibe dieses Kontos" tooltipPosition="bottom">
                                gültig ab {{ headerBankValidFrom | date:'dd.MM.yyyy' }}
                                @if (headerBankValidTo) {
                                    bis {{ headerBankValidTo | date:'dd.MM.yyyy' }}
                                }
                            </span>
                        }
                    }
                </div>
                <!--
                  A PLANNED slice („ab {Datum}: {Bank} {IBAN}", E-7): the bank change already written for a
                  later date. Full IBAN (Q-11c). Nothing is rendered while no planned slice exists.
                -->
                @if (plannedBankSlice) {
                    <div class="flex align-items-center gap-2 text-500 text-sm">
                        <i class="fa-regular fa-calendar-plus text-blue-600"></i>
                        <span>
                            ab {{ plannedBankSlice.startDate | date:'dd.MM.yyyy' }}:
                            {{ plannedBankSlice.bankName || 'Bankverbindung' }}
                            @if (plannedBankSlice.iban) {
                                <span class="font-mono ml-1">{{ plannedBankSlice.iban }}</span>
                            }
                        </span>
                    </div>
                }
                <!--
                  The slice read FAILED (bankSlicesError, base component): said, never hidden - without
                  this the validity line above is simply missing and reads like „no slice" (E-9), which
                  is a different fact. Warn, not error: the account itself loaded.
                -->
                @if (bankSlicesError) {
                    <p-message severity="warn" icon="fa-regular fa-triangle-exclamation">
                        <span class="text-sm">Gültigkeit der Bankverbindung nicht ermittelbar – die Zeitscheiben dieses Kontos konnten nicht geladen werden.</span>
                    </p-message>
                }
            </div>
        </div>
        <!--
          Action bar of the account header.

          The actions are NOT wired one by one in the template: they are rendered from
          accountToolbarButtons (built with WidgetFactory.createToolbarButton) and dispatched by
          their action id through the ONE click seam handleAccountToolbarClick() — the same button
          model and the same action semantics a widget toolbar uses, except that this header is not
          a widget. The pre-existing "Saldo aktualisieren" button was migrated INTO this bar rather
          than left standing next to it.

          The @if on button.display genuinely removes a hidden button from the DOM, whereas
          WidgetHelperButton#display only drops the icon inside a widget toolbar.

          The buttons are ICON-ONLY: no [label], so the bar stays narrow next to the identity block.
          The tooltip is therefore the only VISIBLE text of an action (falling back to the label when
          an action carries none), and [ariaLabel] keeps the action named for screen readers.
        -->
        <div class="flex flex-wrap gap-2">
            @for (button of accountToolbarButtons; track button.action) {
                @if (button.display) {
                    <p-button
                            [icon]="button.icon"
                            [ariaLabel]="button.label"
                            [rounded]="true"
                            severity="secondary"
                            [pTooltip]="getAccountToolbarTooltip(button)"
                            tooltipPosition="bottom"
                            [loading]="isAccountToolbarButtonBusy(button)"
                            (onClick)="handleAccountToolbarClick(button)">
                    </p-button>
                }
            }
        </div>
    </div>

    <!--
      KONTO GESCHLOSSEN - the replacement of the former "Geschlossen" status tag in the title row, made
      prominent on purpose: a clerk working on a closed account must see it before anything else.
      bm.CustomerBillingAccount carries only the flag `closed`; there is no closing date on the entity
      (no closedDate / validTo field), so none is shown.
    -->
    @if (customerBillingAccountDto.closed) {
        <p-message severity="error" icon="fa-solid fa-lock" class="block mb-4">
            <span class="font-bold">Konto geschlossen</span>
        </p-message>
    }

    <!-- SEPA-Integritätsprotokoll Banner -->
    <!-- Source: sepaFailureState (base component, GET …/customerBillingAccounts/{id}/sepaFailureState).
         Four states, checked in this order:
         - read failed: a warning, because the account may have failures nobody can see right now. The
           global error toast is suppressed for this read, so this message is the only signal;
         - first read still running (no state yet): a skeleton in place of the banner;
         - active failure: the banner (activeFailure is exactly "failCount > 0 in the window");
         - no active failure: nothing. -->
    @if (sepaFailureStateError) {
        <p-message severity="warn" icon="fa-regular fa-triangle-exclamation" class="block mb-4">
            SEPA-Status konnte nicht geladen werden.
        </p-message>
    } @else if (sepaFailureStateLoading && !sepaFailureState) {
        <p-skeleton height="3.5rem" class="mb-4"></p-skeleton>
    } @else if (sepaFailureState?.activeFailure) {
        <div class="surface-card border-round-xl shadow-1 border-1 border-red-200 mb-4 p-3 flex align-items-center gap-3">
            <div class="flex align-items-center justify-content-center bg-red-100 border-round-lg p-2">
                <i class="pi pi-exclamation-circle text-red-500 text-xl"></i>
            </div>
            <div class="flex-1">
                <span class="font-bold text-900 text-sm">SEPA-Integritätsprotokoll</span>
                @if (sepaFailureState.lastReturnReasonName) {
                    <span class="text-600 text-sm ml-2">{{ sepaFailureState.lastReturnReasonName }}</span>
                }
                @if (sepaFailureState.lastFailDate) {
                    <span class="text-400 text-xs ml-2">({{ sepaFailureState.lastFailDate | date:'dd.MM.yyyy' }})</span>
                }
            </div>
            <span class="bg-red-500 text-white text-xs px-2 py-1 border-round font-bold">
                {{ sepaFailureState.failCount }} FEHLER
            </span>
        </div>
    }

    <!--
      MAHNSTATUS-BAND — a full-width band in the gap between the account header and the
      Finanzkennzahlen, the position the annotation's arrow points at. Sibling of the
      SEPA-Integritätsprotokoll banner above and built to the same rhythm: icon chip, flex-1 text
      run, trailing „Mahnung anzeigen" on the right.

      CONTENT IS DELIBERATELY EXACTLY THE OLD DEV CARD'S FOUR FACTS PLUS THE ACTION: status,
      Mahnstufe (as its readable name — not the raw id the dev card wrongly showed), Mahnungstyp,
      Startsaldo, „Mahnung anzeigen". Design and position are ours; the content is not to grow.
      Mahnpfad, „seit"-Datum, the Frist tag and the hand-over/Mahnstopp/pending qualifier line were
      all removed by that decision — do not argue them back in here. Everything shown arrives with
      the account load; no extra request.

      ONE LINE at xl+; below xl the button becomes its own right-aligned row and the text run may
      fold, with wraps falling between segments.

      SCOPE — the band summarises, it does not become the dunning object. Whoever needs more than
      the four facts follows „Mahnung anzeigen", which opens the full dunning object in place via
      breadcrumb.

      NO ACTIVE DUNNING => NO BAND AT ALL. No placeholder, no hint — an account without an active
      dunning simply does not carry this band.
    -->
    @if (runningDunning?.dunningId) {
        @let dunning = runningDunning;

        <div class="surface-card border-round-xl shadow-1 border-1 border-orange-200 mb-4 p-3 flex flex-wrap align-items-center gap-2 md:gap-3">

            <div class="flex align-items-center justify-content-center border-round-lg p-2 bg-orange-100">
                <i class="pi pi-bell text-xl text-orange-500"></i>
            </div>

            <!--
              min-w-0 is LOAD-BEARING: a flex item's min-width defaults to auto, so without it this
              flex-1 run refuses to shrink below its content width — the row then overflows
              horizontally and (at xl+, where „Mahnung anzeigen" sits inline) pushes it out of the
              card, instead of letting the segments wrap inside the run.

              THE RUN IS A FLEX-WRAP CONTAINER, NOT INLINE FLOW, and that is load-bearing too.
              Angular compiles templates with preserveWhitespaces: false (no override anywhere in
              this workspace), which REMOVES the whitespace-only text nodes between these spans —
              and in inline flow those text nodes were the ONLY soft-wrap opportunities left once
              every segment span became white-space-nowrap. The result was one unbreakable string
              clipped at the card edge. As flex items the segments wrap at item boundaries by
              definition, independent of whitespace, so neither Angular's whitespace handling nor a
              future nowrap segment can silently break the wrapping again. gap-1 spaces the items;
              the former mx-1/ml-1/ml-2 margins are gone — keeping both would double the spacing.
            -->
            <div class="flex-1 min-w-0 flex flex-wrap align-items-center gap-1">
                <span class="font-bold text-900 text-sm">Mahnstatus</span>

                <!--
                  The STATUS as a tag — our design's rendering of the dev card's „Aktive Mahnung"
                  heading. The enum's actual labels are title-case („Aktiv" / „Ausgesetzt" /
                  „Wartend" / „Abgeschlossen") and never hardcoded here; the `uppercase` utility
                  applies the ALL-CAPS purely presentationally. One colour for all states: the
                  qualifier machinery (hand-over/Mahnstopp) that once varied it was removed with
                  the content reduction, and mapping the raw status ordinal in the view would be
                  worse than a steady orange.

                  WHY THE COLOUR RIDES IN styleClass: [severity] is currently INERT app-wide.
                  primeng-v20-overrides.scss:6319 declares `.p-tag { background: var(--primary-800) }`
                  unconditionally, at the same specificity (0,1,0) as PrimeNG's generated
                  .p-tag-warn/-danger/-secondary and loaded AFTER them — source order wins, so every
                  tag in the app renders primary-800 blue whatever [severity] says. The PrimeFlex
                  colour utility below carries !important and beats that override. [severity] STAYS
                  because it is what the code MEANS — if the global override is ever fixed it takes
                  over again; the styleClass colour is a local workaround, not the intent.
                -->
                @if (dunning.statusLabel) {
                    <p-tag [value]="dunning.statusLabel"
                           severity="warn"
                           styleClass="text-xs font-bold uppercase text-white bg-orange-500">
                    </p-tag>
                }

                <!--
                  THE MAHNSTUFE, read off the CONFIG level (bd.DunningTypeLevel), never off the
                  runtime one. activeLevelDtoName is deliberately NOT in this chain, not even as a
                  fallback: bd.DunningLevel carries no name, so that field is the raw entity id —
                  measured as literally "3802" on dev account 54902, where this hop resolves to
                  „Stufe 2 (Qualifizierte Mahnung)".
                -->
                @if (dunning.levelName) {
                    <span class="text-500">·</span>
                    <span class="text-sm font-semibold text-orange-600 white-space-nowrap">
                        {{ dunning.levelName }}
                    </span>
                }

                <!--
                  THE MAHNUNGSTYP — dunningTypeDtoName, the configured process
                  („Standard-Mahnverfahren (Pfad A/B/C)"). NOT the Mahnpfad (the bd.BdDunningPath
                  enum, no longer on the DTO) — the two were conflated here once; the Mahnpfad is
                  off the band by the content reduction, keep the distinction straight if either
                  ever returns.

                  white-space-nowrap on EVERY value segment of this run: wraps must fall BETWEEN
                  the ·-separated segments, never inside one — a Mahnungstyp breaking as
                  „Standard-" / „Mahnverfahren (Pfad" / „A/B/C)" is what this prevents.
                -->
                @if (dunning.dunningTypeName) {
                    <span class="text-500">·</span>
                    <span class="text-500 text-sm white-space-nowrap">{{ dunning.dunningTypeName }}</span>
                }

                <!--
                  != null ON PURPOSE: a Startsaldo of exactly 0 is a legitimate value, and a
                  truthiness guard would silently swallow it. The currency pipe follows the same
                  convention as the Finanzkennzahlen below; the dunning's own balanceOnStartCurrency
                  wins, the account currency is only the fallback.
                -->
                @if (dunning.balanceOnStart != null) {
                    <span class="text-600 text-sm white-space-nowrap">
                        (Startsaldo: {{ dunning.balanceOnStart | currency: (dunning.balanceOnStartCurrency || customerBillingAccountDto.currency) }})
                    </span>
                }

            </div>

            <!--
              THE ACTION — deliberately OUTSIDE the text run so it never floats inside wrapped
              prose. Below xl (1200) the wrapper is w-full and becomes its own right-aligned row;
              at xl+ it sits inline on the right. flex-shrink-0 keeps it from being squeezed
              while inline.
            -->
            <div class="flex align-items-center gap-2 w-full xl:w-auto justify-content-end flex-shrink-0">
                <p-button label="Mahnung anzeigen" [text]="true" size="small" icon="pi pi-arrow-right"
                          (onClick)="openDunningObject()"></p-button>
            </div>
        </div>
    }

    <!--
      Financial metrics — ALWAYS full width, and PURELY financial: four tiles, Gesamtsaldo / Aktiver
      Saldo / Aktueller Saldo / Beiträge Aktuell.
      The former separate 1/3-width dunning card is gone, and so is the Mahnstatus TILE that briefly
      replaced it: the annotation marked a POSITION on the page — the gap above this card — not a
      slot inside it. The Mahnstatus is a band up there now, next to the SEPA banner. What this card
      keeps from that round is the full width: it no longer gives up a third of the row for exactly
      the accounts that need the most room. It also lost its header strip (title + currency line) to
      win the vertical space back; the currency is on every amount below anyway.
    -->
    <div class="grid mb-4">
        <div class="col-12">
            <div class="surface-card border-round-xl shadow-1 border-1 surface-border h-full">
                <div class="p-4">
                    <div class="flex flex-wrap gap-4">
                        <!-- Gesamtsaldo -->
                        <div class="flex-1 flex flex-column gap-2 border-round-lg p-4"
                             [ngClass]="(customerBillingAccountDto.customerBillingAccountBalanceDto?.balance || 0) < 0 ? 'bg-red-50 border-1 border-red-100' : 'bg-green-50 border-1 border-green-100'">
                            <p class="text-600 text-sm font-medium uppercase tracking-wide m-0">Gesamtsaldo</p>
                            <p class="m-0 text-2xl font-bold"
                               [ngClass]="(customerBillingAccountDto.customerBillingAccountBalanceDto?.balance || 0) < 0 ? 'text-red-600' : 'text-green-600'">
                                {{ customerBillingAccountDto.customerBillingAccountBalanceDto?.balance || 0 | currency: customerBillingAccountDto.currency }}
                            </p>
                            <div class="flex align-items-center gap-1 text-xs font-semibold"
                                 [ngClass]="(customerBillingAccountDto.customerBillingAccountBalanceDto?.balance || 0) < 0 ? 'text-red-700' : 'text-green-700'">
                                <i class="pi text-xs" [ngClass]="(customerBillingAccountDto.customerBillingAccountBalanceDto?.balance || 0) < 0 ? 'pi-arrow-down' : 'pi-arrow-up'"></i>
                                @if ((customerBillingAccountDto.customerBillingAccountBalanceDto?.balance || 0) < 0) {
                                    Negativer Saldo
                                } @else {
                                    Saldo OK
                                }
                            </div>
                        </div>

                        <!-- Aktiver Saldo -->
                        <div class="flex-1 flex flex-column gap-2 border-round-lg p-4 bg-blue-50 border-1 border-blue-100">
                            <p class="text-600 text-sm font-medium uppercase tracking-wide m-0">Aktiver Saldo</p>
                            <p class="m-0 text-2xl font-bold text-primary">
                                {{ customerBillingAccountDto.customerBillingAccountBalanceDto?.activeBalance || 0 | currency: customerBillingAccountDto.currency }}
                            </p>
                            <div class="flex align-items-center gap-1 text-blue-700 text-xs font-semibold">
                                <i class="pi pi-calendar text-xs"></i>
                                Ausstehende Transaktionen
                            </div>
                        </div>

                        <!-- Aktueller Saldo -->
                        <div class="flex-1 flex flex-column gap-2 border-round-lg p-4"
                             [ngClass]="(customerBillingAccountDto.customerBillingAccountBalanceDto?.currentBalance || 0) < 0 ? 'bg-orange-50 border-1 border-orange-100' : 'surface-100 border-1 surface-border'">
                            <p class="text-600 text-sm font-medium uppercase tracking-wide m-0">Aktueller Saldo</p>
                            <p class="m-0 text-2xl font-bold"
                               [ngClass]="(customerBillingAccountDto.customerBillingAccountBalanceDto?.currentBalance || 0) < 0 ? 'text-orange-600' : 'text-900'">
                                {{ customerBillingAccountDto.customerBillingAccountBalanceDto?.currentBalance || 0 | currency: customerBillingAccountDto.currency }}
                            </p>
                            @if (customerBillingAccountDto.customerBillingAccountBalanceDto?.negativeSince) {
                                <div class="flex align-items-center gap-1 text-orange-700 text-xs font-semibold">
                                    <i class="pi pi-exclamation-circle text-xs"></i>
                                    Negativ seit {{ customerBillingAccountDto.customerBillingAccountBalanceDto.negativeSince | date:'dd.MM.yyyy' }}
                                </div>
                            }
                        </div>

                        <!-- Beiträge Aktuell -->
                        <div class="flex-1 flex flex-column gap-2 border-round-lg p-4 surface-100 border-1 surface-border">
                            <p class="text-600 text-sm font-medium uppercase tracking-wide m-0">Beiträge Aktuell</p>
                            <p class="m-0 text-2xl font-bold text-900">
                                {{ (contractPriceTotalCurrent ?? 0) | currency: customerBillingAccountDto.currency }}
                            </p>
                            @if (contractPriceTotalFuture != null && contractPriceTotalFuture !== contractPriceTotalCurrent) {
                                <div class="text-xs text-blue-700 font-semibold">
                                    Zukünftig: {{ contractPriceTotalFuture | currency: customerBillingAccountDto.currency }}
                                </div>
                            }
                            <p-button label="Details" [text]="true" size="small" icon="pi pi-external-link"
                                      (onClick)="openContractPriceDetail()"></p-button>
                        </div>
                    </div>

                    <!--
                        TM-1000001932: Saldoneutrale "Vorausbuchung angenommener
                        Stornos/Wiederinkraftsetzungen" der aktiven Mahnung.
                        SEPARAT ausgewiesen — der rohe Gesamt-/Aktuelle Saldo bleibt
                        UNVERÄNDERT. Quelle: BD-Tracker (Betrag + Kommentar) der
                        aktiven Mahnung dieses Kontos.
                        Das Laden wird Daten-getrieben über den Hook
                        onCustomerBillingAccountLoaded() (Subscribe-Completion)
                        angestoßen — KEIN Methodenaufruf in der Interpolation mehr.
                        Das Template bindet ausschließlich an Felder.
                    -->
                    @if (runningDunning?.dunningId) {
                        @if (anticipatedStornoLoading) {
                            <div class="flex align-items-center gap-2 text-600 text-sm mt-4 pt-4 border-top-1 surface-border">
                                <i class="fa-regular fa-spinner fa-spin"></i>
                                <span>Vorausbuchung wird geladen …</span>
                            </div>
                        } @else if (hasAnticipatedStornoBooking) {
                            <div class="mt-4 pt-4 border-top-1 surface-border">
                                <div class="flex align-items-center gap-2 mb-3">
                                    <i class="fa-regular fa-clock-rotate-left text-blue-600"></i>
                                    <span class="text-700 text-sm font-semibold">
                                        Vorausbuchung angenommener Stornos/Wiederinkraftsetzungen
                                    </span>
                                    @if (anticipatedStornoBooking?.comment) {
                                        <i class="fa-regular fa-circle-info text-500 text-sm cursor-help"
                                           [pTooltip]="anticipatedStornoBooking.comment"
                                           tooltipPosition="top"></i>
                                    }
                                </div>

                                <div class="flex flex-wrap align-items-stretch gap-2">
                                    <!-- Roher Saldo (unverändert) -->
                                    <div class="flex flex-column gap-1 border-round-lg p-3 surface-100 border-1 surface-border">
                                        <span class="text-600 text-xs font-medium uppercase tracking-wide">Saldo</span>
                                        <span class="text-lg font-bold"
                                              [ngClass]="rawBalance < 0 ? 'text-red-600' : 'text-900'">
                                            {{ rawBalance | currency: anticipatedStornoCurrency }}
                                        </span>
                                    </div>

                                    <div class="flex align-items-center text-600 text-xl">+</div>

                                    <!-- Vorausbuchung (zukunftsgerichtet, neutral) -->
                                    <div class="flex flex-column gap-1 border-round-lg p-3 bg-blue-50 border-1 border-blue-100">
                                        <span class="text-600 text-xs font-medium uppercase tracking-wide">Vorausbuchung</span>
                                        <span class="text-lg font-bold text-blue-700">
                                            {{ anticipatedStornoAmount | currency: anticipatedStornoCurrency }}
                                        </span>
                                        <span class="text-500 text-xs">voraussichtlich, nächster Lauf</span>
                                    </div>

                                    <div class="flex align-items-center text-600 text-xl">=</div>

                                    <!-- Effektiver Saldo nach Vorausbuchung (Ausblick) -->
                                    <div class="flex flex-column gap-1 border-round-lg p-3"
                                         [ngClass]="effectiveBalanceAfterAnticipated < 0 ? 'bg-orange-50 border-1 border-orange-100' : 'bg-green-50 border-1 border-green-100'">
                                        <span class="text-600 text-xs font-medium uppercase tracking-wide">Effektiver Saldo (nach Vorausbuchung)</span>
                                        <span class="text-lg font-bold"
                                              [ngClass]="effectiveBalanceAfterAnticipated < 0 ? 'text-orange-600' : 'text-green-600'">
                                            {{ effectiveBalanceAfterAnticipated | currency: anticipatedStornoCurrency }}
                                        </span>
                                    </div>
                                </div>

                                <p class="text-500 text-xs mt-2 mb-0">
                                    <i class="fa-regular fa-circle-info mr-1"></i>
                                    Saldoneutrale Vorausbuchung — der ausgewiesene Saldo bleibt unverändert; die echte Gutschrift folgt im nächsten Abrechnungslauf.
                                </p>
                            </div>
                        } @else if (anticipatedStornoError) {
                            <div class="flex align-items-center gap-2 text-red-600 text-sm mt-4 pt-4 border-top-1 surface-border">
                                <i class="fa-regular fa-triangle-exclamation"></i>
                                <span>Vorausbuchung konnte nicht geladen werden.</span>
                            </div>
                        }
                    }
                </div>
            </div>
        </div>

    </div>

    <!-- Verlauf-Tabs Bereich -->
    <div class="surface-card border-round-xl shadow-1 border-1 surface-border">
        <!-- Tab-Überschriften -->
        <p-tabs [(value)]="activeTabIndex">
            <p-tablist>
                <p-tab [value]="0">
                    <i class="pi pi-list mr-2"></i>
                    Transaktionen
                </p-tab>
                <p-tab [value]="1">
                    <i class="pi pi-file mr-2"></i>
                    Verträge
                    <!--
                      Counts what the tab actually SHOWS: the contract rows (so the badge follows the
                      toggle) plus the open applications below them, which the toggle does not touch.
                    -->
                    @if (contractsTabCount) {
                        <p-badge [value]="contractsTabCount.toString()" severity="secondary" styleClass="ml-2"></p-badge>
                    }
                </p-tab>
                <!--
                  Round 11 (WP-B): the former „IBAN-Verlauf" tab became „Bankverbindungen & IBANs" - the
                  bank slices, the debit IBANs actually used, the payer IBANs (the old table, moved
                  unchanged) and the account messages. The badge counts the slices; a SEPA account
                  without a slice valid today additionally carries a warning icon.
                -->
                <p-tab [value]="2">
                    <i class="fa-regular fa-building-columns mr-2"></i>
                    Bankverbindungen &amp; IBANs
                    @if (bankSlices.length) {
                        <p-badge [value]="bankSlices.length.toString()" severity="secondary" styleClass="ml-2"></p-badge>
                    }
                    @if (!bankSlicesLoading && !bankSlicesError && !currentBankSlice && isSepaDirectDebitAccount()) {
                        <i class="fa-solid fa-triangle-exclamation text-orange-500 ml-2"
                           pTooltip="Keine Bankverbindung hinterlegt – der SEPA-Schritt des nächsten Laufs bricht ab"
                           tooltipPosition="bottom"></i>
                    }
                </p-tab>
                <!--
                  projects/bm-account-history: the change timeline of the account, of its bank slices
                  and of the bank accounts they referenced. No badge - the number is only known after
                  the load.
                -->
                <p-tab [value]="3">
                    <i class="fa-regular fa-clock-rotate-left mr-2"></i>
                    Verlauf
                </p-tab>
            </p-tablist>
        </p-tabs>

        <!-- Tab-Inhalt: Transaktionen -->
        @if (activeTabIndex === 0) {
            <div class="p-3">
                <bm-customer-account-transaction
                    [billingAccountId]="customerBillingAccountDto.id"
                    [customerId]="customerBillingAccountDto.customerDtoId"
                    [pageSize]="20"
                    direction="desc">
                </bm-customer-account-transaction>
            </div>
        }

        <!-- Tab-Inhalt: Verträge -->
        @if (activeTabIndex === 1) {
            <div class="p-3">
                <!--
                  Expired-contract switch — purely client side. Every contract is already loaded;
                  the switch only decides whether the ENDED slices are rendered too - an assignment
                  whose own end („Zugeordnet bis") or whose contract's end („Vertragsende") lies
                  before today. A slice that has not started yet is always shown, tagged „ab {Datum}"
                  in the status column (D-R11-1: it used to be hidden and counted as „beendet"). The
                  former server round-trip is gone: it filtered the endDate of the link row, which the
                  framework high-dates, so it matched everything and did nothing.

                  (onChange) recomputes the row set. It is NOT redundant next to [(ngModel)]: the
                  rendered list is a stored array, not a getter, so nothing would recompute on its
                  own. PrimeNG writes the model before it emits onChange (ToggleSwitch.onClick calls
                  onModelChange one line above onChange.emit), so includeOldContracts already holds
                  the new value here.

                  PRECONDITION for that ordering: the default updateOn: 'change'. It is what makes
                  Angular run updateControl — and with it the write into includeOldContracts —
                  synchronously inside the onModelChange callback. Setting
                  [ngModelOptions]="{updateOn: 'blur'}" (or 'submit') on this switch would defer the
                  write past the emit, and the handler would then recompute from the PREVIOUS value,
                  leaving the list one click behind. If updateOn is ever changed here, read the new
                  value off the event ($event.checked) instead of the field.
                -->
                <div class="flex align-items-center justify-content-between mb-4">
                    <div class="flex align-items-center gap-3">
                        <p-toggleSwitch [(ngModel)]="includeOldContracts"
                                        (onChange)="recomputeVisibleContractPaymentAccounts()"></p-toggleSwitch>
                        <span class="text-sm text-600">Abgelaufene Verträge anzeigen</span>
                        <!--
                          Says how many rows the switch hides (round 11, D6b remainder) - exactly the
                          ENDED ones, never a future slice (D-R11-1): a merge preview may plan more
                          contracts than this tab shows, and the difference is exactly the ended ones
                          hidden here.
                        -->
                        @if (hiddenEndedContractCount > 0) {
                            <span class="text-sm text-500">({{ hiddenEndedContractCount }} beendete ausgeblendet)</span>
                        }
                    </div>
                </div>

                <!--
                  TM-1000002026: one row per ASSIGNMENT slice (bm.ContractPaymentAccount), the slices of one
                  contract next to each other (base: bmSortContractLinks - VSNR, then „Zugeordnet ab").
                  Every cell text and tag style is precomputed in contractLinkRows (bm-contract-link-rows),
                  so the template binds primitives of a stable array only.
                  Columns: VSNR | Partner | Typ | Frequenz | Beitrag | Zugeordnet ab | Zugeordnet bis |
                  Vertragsende | Status (the former column „Vertrag" - the contract's id/name - is gone).
                  - Partner / Typ: SHORT name, else the long name; the long name is the tooltip.
                  - Frequenz: the assignment's own payment frequency.
                  - Zugeordnet ab / bis: the assignment's OWN period; the open end 31.12.9999 reads
                    „unbefristet". Vertragsende: the contract's end, „Laufend" without one.
                  - Status: the contract's base status in the colours of its enum, carried by styleClass -
                    [severity] is inert app-wide (see the dunning tag in the header); next to it „ab {Datum}"
                    for a slice that has not started yet or „bis {Datum}" for a slice this account no longer
                    bills, coloured the same way.
                  The expansion is keyed per assignment (cpa.id): two slices of one contract open separately.
                -->
                @if (contractLinkRows.length) {
                    <div class="border-round-lg border-1 surface-border overflow-hidden">
                        <!-- TM-1000002026: rows tracked by assignment id - an expanded row keeps its widgets -->
                        <p-table
                            [value]="contractLinkRows"
                            [rowTrackBy]="contractLinkRowTrackBy"
                            [scrollable]="true"
                            styleClass="p-datatable-sm">
                            <ng-template #header>
                                <tr class="bg-gray-50">
                                    <th class="font-bold text-xs uppercase text-500 w-3rem"></th>
                                    <th class="font-bold text-xs uppercase text-500">VSNR</th>
                                    <th class="font-bold text-xs uppercase text-500">Partner</th>
                                    <th class="font-bold text-xs uppercase text-500">Typ</th>
                                    <th class="font-bold text-xs uppercase text-500">Frequenz</th>
                                    <th class="font-bold text-xs uppercase text-500 text-right">Beitrag</th>
                                    <th class="font-bold text-xs uppercase text-500">Zugeordnet ab</th>
                                    <th class="font-bold text-xs uppercase text-500">Zugeordnet bis</th>
                                    <th class="font-bold text-xs uppercase text-500">Vertragsende</th>
                                    <th class="font-bold text-xs uppercase text-500">Status</th>
                                </tr>
                            </ng-template>
                            <ng-template #body let-row>
                                @let cpa = row.link;
                                <!--
                                  The VSNR cell is the SINGLE trigger that opens the cm.Contract in
                                  the left object bar (MvsObjectNavigationService). It is rendered
                                  as an underlined link so the affordance is visible — a clickable
                                  row looks like plain text and gives the user nothing to aim at.
                                  The row itself carries no click handler.
                                -->
                                <tr class="hover:surface-hover">
                                    <td>
                                        <!-- TM-1000002026: Font Awesome chevrons (fa-solid for state) -->
                                        <p-button
                                            [icon]="isContractRowExpanded(cpa)
                                                ? 'fa-solid fa-chevron-down'
                                                : 'fa-solid fa-chevron-right'"
                                            [text]="true"
                                            [rounded]="true"
                                            severity="secondary"
                                            (onClick)="toggleContractRow(cpa); $event.stopPropagation()">
                                        </p-button>
                                    </td>
                                    <td class="text-600">
                                        <!--
                                          No href ON PURPOSE: a real one would let a plain click
                                          navigate the browser away from the account. That costs the
                                          keyboard, so role/tabindex/Enter put it back.
                                          A missing VSNR stays a plain dash — nothing to open.
                                        -->
                                        @if (cpa.contractDto?.contractIdentifier) {
                                            <a class="underline cursor-pointer text-primary"
                                               role="link"
                                               tabindex="0"
                                               [attr.aria-label]="'Vertrag ' + cpa.contractDto.contractIdentifier + ' öffnen'"
                                               (click)="openContract(cpa, $event)"
                                               (keydown.enter)="openContract(cpa)">{{ cpa.contractDto.contractIdentifier }}</a>
                                        } @else {
                                            -
                                        }
                                    </td>
                                    <td class="text-600" [pTooltip]="row.partnerTooltip" tooltipPosition="top">
                                        {{ row.partnerLabel }}
                                    </td>
                                    <td class="text-600" [pTooltip]="row.contractTypeTooltip" tooltipPosition="top">
                                        {{ row.contractTypeLabel }}
                                    </td>
                                    <td class="text-600">{{ row.frequencyLabel }}</td>
                                    <td class="text-right font-medium white-space-nowrap">
                                        @if (cpa.contractDto?.contractPrice?.paymentAmount != null) {
                                            {{ cpa.contractDto.contractPrice.paymentAmount | number:'1.2-2' }} €
                                        } @else if (cpa.contractDto?.contractPrice?.priceGross != null) {
                                            {{ cpa.contractDto.contractPrice.priceGross | number:'1.2-2' }} €
                                        } @else {
                                            -
                                        }
                                    </td>
                                    <td class="text-600 white-space-nowrap">{{ row.assignedFromLabel }}</td>
                                    <td class="text-600 white-space-nowrap">{{ row.assignedToLabel }}</td>
                                    <td class="text-600 white-space-nowrap">
                                        @if (row.contractEndLabel) {
                                            {{ row.contractEndLabel }}
                                        } @else {
                                            <span class="text-green-600 font-semibold">Laufend</span>
                                        }
                                    </td>
                                    <td>
                                        <div class="flex flex-wrap align-items-center gap-1">
                                            <p-tag [value]="row.statusTag.label"
                                                   [icon]="row.statusTag.icon"
                                                   [styleClass]="row.statusTag.styleClass">
                                            </p-tag>
                                            <!--
                                              „ab {Datum}" - a slice that has not started yet (D-R11-1): the
                                              status tag says what the CONTRACT is, this tag from when THIS
                                              account bills it. „bis {Datum}" - a slice this account no
                                              longer bills (only with the switch on): the day this assignment
                                              stopped billing the contract.
                                            -->
                                            @if (row.assignmentTag; as assignmentTag) {
                                                <p-tag [value]="assignmentTag.label"
                                                       [styleClass]="assignmentTag.styleClass"
                                                       [pTooltip]="assignmentTag.tooltip"
                                                       tooltipPosition="top">
                                                </p-tag>
                                            }
                                        </div>
                                    </td>
                                </tr>
                                @if (isContractRowExpanded(cpa)) {
                                    <tr class="bg-surface-50">
                                        <td [attr.colspan]="10" class="p-3">
                                            <div class="flex flex-column gap-3">
                                                <div>
                                                    <div class="font-semibold text-sm mb-2">
                                                        <i class="fa-regular fa-money-bill mr-1"></i> Beiträge
                                                    </div>
                                                    @if (contractPriceWidgets[cpa.id]) {
                                                        <mvs-widget
                                                            [widgetData]="contractPriceWidgets[cpa.id]">
                                                        </mvs-widget>
                                                    }
                                                </div>
                                                <div>
                                                    <div class="font-semibold text-sm mb-2">
                                                        <i class="fa-regular fa-calendar mr-1"></i> Geplante Zahlungen
                                                    </div>
                                                    @if (contractPaymentPlannedWidgets[cpa.id]) {
                                                        <mvs-widget
                                                            [widgetData]="contractPaymentPlannedWidgets[cpa.id]">
                                                        </mvs-widget>
                                                    }
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                }
                            </ng-template>
                            <ng-template #emptymessage>
                                <tr>
                                    <td colspan="10" class="text-center p-5 text-500">
                                        <i class="fa-regular fa-inbox text-4xl mb-3 block text-300"></i>
                                        Keine Verträge mit diesem Abrechnungskonto verknüpft
                                    </td>
                                </tr>
                            </ng-template>
                        </p-table>
                    </div>
                } @else {
                    <!--
                      Empty state: distinguishes "no contracts at all" from "all of them ended and
                      are therefore hidden" — otherwise an account holding only ended contracts would
                      look exactly like an account holding none. A future slice never lands here: it
                      is shown (D-R11-1).
                    -->
                    <div class="text-center p-5 text-500 surface-100 border-round-lg">
                        <!-- TM-1000002026: Font Awesome like the rest of the table block -->
                        <i class="fa-regular fa-inbox text-4xl mb-3 block text-300"></i>
                        @if (customerBillingAccountDto.contractPaymentAccounts?.length) {
                            <p class="m-0">Keine laufenden oder künftigen Verträge — „Abgelaufene Verträge anzeigen“ einschalten, um die beendeten zu sehen</p>
                        } @else {
                            <p class="m-0">Keine Verträge mit diesem Abrechnungskonto verknüpft</p>
                        }
                    </div>
                }

                <!--
                  „Offene Anträge (noch kein Vertrag)“ (projects/bm-billing-account-open-cw-cases): the
                  cw product lines whose Vorgang pays through this account, „Einreichbar“ or „Wartet auf
                  Policierung“, not converted into a contract yet - rule and read in
                  bm-open-cw-case-lines-ql.ts. A line is either a contract row above or a row here,
                  never both. The switch above does not act on this section.

                  Its OWN states, so the contract table above is never touched by them: a hint while the
                  first read runs, a short message when it fails (also a missing cw permission - the
                  global toast is off for this read), and NOTHING at 0 rows - the empty state above
                  stays the only one. Read-only: the Vorgangsnummer opens the Vorgang in the left object
                  bar, like the VSNR above opens the contract; no row expansion.
                -->
                @if (openCwCaseLinesError) {
                    <p-message severity="warn" icon="fa-regular fa-triangle-exclamation" class="block mt-4">
                        Offene Anträge konnten nicht geladen werden – Fehler oder keine Berechtigung für Vorgänge.
                    </p-message>
                } @else if (openCwCaseLinesLoading && !openCwCaseLines.length) {
                    <div class="flex align-items-center gap-2 mt-4 text-sm text-500">
                        <p-skeleton shape="circle" size="1rem"></p-skeleton>
                        <span>Offene Anträge werden geladen …</span>
                    </div>
                } @else if (openCwCaseLines.length) {
                    <div class="mt-4">
                        <div class="flex align-items-center gap-2 mb-1">
                            <i class="fa-regular fa-file-signature text-500"></i>
                            <span class="font-semibold">Offene Anträge (noch kein Vertrag)</span>
                            <p-badge [value]="openCwCaseLines.length.toString()" severity="secondary"></p-badge>
                        </div>
                        <p class="text-sm text-500 mt-0 mb-2">
                            Vorgänge in „Einreichbar“ oder „Wartet auf Policierung“, die über dieses Abrechnungskonto
                            laufen und noch nicht in einen Vertrag umgewandelt wurden.
                        </p>
                        <div class="border-round-lg border-1 surface-border overflow-hidden">
                            <p-table
                                [value]="openCwCaseLines"
                                [scrollable]="true"
                                dataKey="lineId"
                                styleClass="p-datatable-sm">
                                <ng-template #header>
                                    <tr class="bg-gray-50">
                                        <th class="font-bold text-xs uppercase text-500">Produkt</th>
                                        <th class="font-bold text-xs uppercase text-500">Vorgangsnummer</th>
                                        <th class="font-bold text-xs uppercase text-500">Gesellschaft</th>
                                        <th class="font-bold text-xs uppercase text-500">Typ</th>
                                        <th class="font-bold text-xs uppercase text-500 text-right">Beitrag</th>
                                        <th class="font-bold text-xs uppercase text-500">Beginn</th>
                                        <th class="font-bold text-xs uppercase text-500">Phase</th>
                                        <th class="font-bold text-xs uppercase text-500">Angelegt</th>
                                    </tr>
                                </ng-template>
                                <ng-template #body let-line>
                                    <tr class="hover:surface-hover">
                                        <td class="font-medium">{{ line.productName || '-' }}</td>
                                        <td class="text-600">
                                            <!--
                                              The SINGLE trigger, as the VSNR link above: no href (a plain
                                              click must not navigate the browser away), role/tabindex/Enter
                                              for the keyboard. Without a Vorgangsnummer the Vorgang id
                                              stands in - the line always has a Vorgang to open.
                                            -->
                                            <a class="underline cursor-pointer text-primary"
                                               role="link"
                                               tabindex="0"
                                               [attr.aria-label]="line.caseAriaLabel"
                                               (click)="openCwCase(line, $event)"
                                               (keydown.enter)="openCwCase(line)">{{ line.caseLabel }}</a>
                                        </td>
                                        <td class="text-600">{{ line.partnerName || '-' }}</td>
                                        <td class="text-600">{{ line.contractTypeName || '-' }}</td>
                                        <td class="text-right font-medium">
                                            @if (line.grossPremium != null) {
                                                {{ line.grossPremium | number:'1.2-2' }} {{ line.currencyLabel }}
                                            } @else {
                                                -
                                            }
                                        </td>
                                        <td class="text-600">
                                            @if (line.startDate) {
                                                {{ line.startDate | date:'dd.MM.yyyy' }}
                                            } @else {
                                                -
                                            }
                                        </td>
                                        <td>
                                            <!--
                                              Label, icon and colours are the phase enum's own (resolveEnums:
                                              'resolved'), carried by styleClass: [severity] is inert app-wide
                                              (see the dunning tag in the header), the PrimeFlex colour
                                              utilities are !important and win. Neutral grey when the enum
                                              brought no colours.
                                            -->
                                            @if (line.phaseLabel) {
                                                <p-tag [value]="line.phaseLabel"
                                                       [icon]="line.phaseIcon"
                                                       [styleClass]="line.phaseStyleClass">
                                                </p-tag>
                                            } @else {
                                                -
                                            }
                                            @if (line.partiallyBound) {
                                                <!-- Part of the line is bound to a contract already (shown as a contract row); the rest is still open. -->
                                                <p-tag value="Teilweise zugeordnet"
                                                       styleClass="ml-2 text-xs font-bold bg-orange-200 text-orange-800">
                                                </p-tag>
                                            }
                                        </td>
                                        <td class="text-600">
                                            @if (line.createdDate) {
                                                {{ line.createdDate | date:'dd.MM.yyyy' }}
                                            } @else {
                                                -
                                            }
                                        </td>
                                    </tr>
                                </ng-template>
                            </p-table>
                        </div>
                    </div>
                }
            </div>
        }

        <!--
          Tab-Inhalt: Bankverbindungen & IBANs (round 11, WP-B/B1). The payer-IBAN table that used to
          stand here lives on unchanged inside the component (section 3). (onBankChangeWizardClosed)
          reloads the account so the header follows a slice the wizard may have written.
        -->
        @if (activeTabIndex === 2) {
            <div class="p-3">
                <bm-customer-account-bank-connections
                        [accountId]="customerBillingAccountDto.id"
                        [customerId]="customerBillingAccountDto.customerDtoId"
                        [paymentMethod]="customerBillingAccountDto.paymentMethod"
                        [ibanHistory]="customerBillingAccountDto.ibanHistory"
                        [focusSection]="requestedBankConnectionsSection"
                        (onBankChangeWizardClosed)="handleBankChangeWizardClosed()">
                </bm-customer-account-bank-connections>
            </div>
        }

        <!--
          Tab-Inhalt: Verlauf (projects/bm-account-history, V14). Like the other tabs an @if, no p-tabpanel:
          created on every opening, so a reopened tab reloads and shows what the bank-change wizard just
          wrote. The shared history runs INLINE - it does not register with MvsCrudObjectHistoryService (K-8).
          The notice above it is the signed wording (K-5) and stands in every state of the timeline.
        -->
        @if (activeTabIndex === 3) {
            <div class="p-3">
                <p-message severity="info" icon="fa-regular fa-circle-info" class="block mb-3 bm-account-history-notice">
                    {{ historyNotice }}
                </p-message>
                <mvs-crud-object-history
                        mode="inline"
                        [show]="true"
                        objectType="bm.CustomerBillingAccount"
                        [objectId]="customerBillingAccountDto.id"
                        [emptyMessage]="historyEmptyMessage"
                        [objectTypeLabels]="historyObjectTypeLabels"
                        [openEndAttributes]="historyOpenEndAttributes">
                </mvs-crud-object-history>
            </div>
        }
    </div>
    <!-- Contract Price Detail Dialog -->
    <p-dialog header="Verträge & Beiträge" [(visible)]="showContractPriceDetail"
              [modal]="true" [style]="{ width: '90vw', height: '80vh' }" [maximizable]="true">
        @if (showContractPriceDetail && contractPriceDetailWidget) {
            <mvs-widget [widgetData]="contractPriceDetailWidget"></mvs-widget>
        }
    </p-dialog>

    <!--
      „Abrechnungskonto ändern“ dialog — ONE wizard for Zahlweise, Fälligkeitstag and Zahlungsfrequenz,
      started in ACCOUNT mode for the account shown here (design §1.3 / §9.3). FIXED frame (76rem x 85vh),
      deliberately not content-sized: the wizard must not grow and shrink as its steps expand, collapse and
      complete; the body scrolls inside via contentStyle.
      ONE-WAY [visible] + (visibleChange): X / Escape ask the wizard's close guard (refused while a write
      runs). The @if re-creates the host — and with it its own state service — on every open. (onHide) fires
      on EVERY real close and only lowers the flag — no reload of its own (L-7): the wizard announces every
      write as bm.CustomerBillingAccount on the object bus, and the base view's listener re-reads the account,
      so header, SEPA banner and contracts show what was written.
    -->
    <p-dialog
            [visible]="dialogBillingAccountChangeWizard"
            [modal]="true"
            [style]="{width: '76rem', height: '85vh'}"
            [contentStyle]="{overflow: 'auto'}"
            [resizable]="false"
            [draggable]="false"
            [closeOnEscape]="true"
            (visibleChange)="requestBillingAccountChangeWizardClose()"
            (onHide)="handleBillingAccountChangeWizardHidden()"
            header="Abrechnungskonto ändern">

        @if (dialogBillingAccountChangeWizard) {
            <bm-customer-billing-account-change-wizard
                    [customerBillingAccountId]="billingAccountChangeAccountId"
                    (onDialogClose)="closeBillingAccountChangeWizard()">
            </bm-customer-billing-account-change-wizard>
        }

    </p-dialog>
    }
} @else if (!initialized) {
    <!-- Ladezustand -->
    <div class="flex flex-column justify-content-center align-items-center gap-3" style="min-height: 400px;">
        <p-progressSpinner></p-progressSpinner>
        <p class="text-500">Abrechnungskonto wird geladen...</p>
    </div>
} @else {
    <!-- Nicht-gefunden-Zustand: Laden abgeschlossen, aber kein Datensatz -->
    <div class="flex flex-column justify-content-center align-items-center gap-3" style="min-height: 400px;">
        <i class="fa-regular fa-circle-exclamation text-400 text-3xl"></i>
        <p class="text-500">Abrechnungskonto nicht gefunden.</p>
    </div>
}
